import { UIElement } from "../common_ui_elements/ui_element.js";
import { getLinkedProviderInfo, getLinkedSocialProviders, initLinkedProvidersFromServer, LinkedSocialProvider, resetLinkedSocialProviders } from "./account_providers_preview_state.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { ImgID } from "../../image_loader/img_id.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { DialogManager } from "./dialog_manager.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { SaveDialog } from "./save_dialog.js";
import { AuthProvider } from "../../api/dto/auth.dto.js";
import { ApiError } from "../../api/http_client.js";
import { GoogleSignInPlugin } from "../../plugins/play_games_plugin.js";
import { FacebookSignInPlugin } from "../../plugins/facebook_sign_in_plugin.js";
import { ExitFromGameAccountDialog } from "./exit_from_game_account_dialog.js";
import { DisconnectAccountDialog } from "./disconnect_account_dialog.js";
import { FailedDisconnectSocialMediaDialog } from "./failed_disconnect_social_media_dialog.js";
import { AppleSignInPlugin } from "../../plugins/apple_sign_in_plugin.js";
import { AppleWebSignIn } from "../../plugins/apple_web_sign_in.js";
import { DeleteAccountDialog } from "./delete_account_dialog.js";
import { disconnectProvider as disconnectProviderApi } from "../../api/requests/auth.requests.js";


enum SocialSectionMode
{
  Linked,
  Empty
}

class UISocialMediaSection extends UIElement
{
  private mode: SocialSectionMode;
  private provider?: LinkedSocialProvider;
  private display_name?: string;
  private display_id?: string;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    mode: SocialSectionMode,
    provider?: LinkedSocialProvider,
    on_click?: () => void,
    display_name?: string,
    display_id?: string,
  )
  {
    super( ctx, x, y, w, h, false, !!on_click );
    this.mode = mode;
    this.provider = provider;
    this.display_name = display_name;
    this.display_id = display_id;
    this.createElements();

    if ( on_click )
      this.setOnClick( on_click );
  }

  private createElements(): void
  {
    if ( this.mode === SocialSectionMode.Linked && this.provider !== undefined )
    {
      this.createSocialMediaSectionBg();
      this.createSocialMediaIcon();
      this.createSocialMediaText();
      return;
    }

    this.createEmptySectionBg();
    this.createPlusIcon();
  }

  private createSocialMediaSectionBg(): void
  {
    const social_media_section_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "rgba(255, 255, 255, 0.3)",
    );

    this.addChild( social_media_section_background, true );
  }

  private createSocialMediaIcon(): void
  {
    if ( this.provider === undefined ) return;

    const icon_x = LayoutScaler.xFromDesign( 29 );
    const icon_size = LayoutScaler.widthFromDesign( 60 );
    const icon_y = ( this.rect.getH() - icon_size ) / 2;

    const social_media_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      this.getProviderIcon( this.provider )
    );

    this.addChild( social_media_icon, true );
  }

  private createSocialMediaText(): void
  {
    if ( this.provider === undefined ) return;

    const text_x = LayoutScaler.xFromDesign( 120 );
    const text_w = LayoutScaler.widthFromDesign( 610 );
    const name_h = LayoutScaler.heightFromDesign( 45 );
    const name_y = LayoutScaler.yFromDesign( 15 );

    if ( this.display_name )
    {
      const name_text = new UITextElement(
        this.ctx,
        text_x,
        name_y,
        text_w,
        name_h,
        {
          text: this.display_name,
          vertical_align: MyTextVerticalAlign.Center,
          text_align: MyTextAlign.Left,
          text_style: TextStyleScheme.rubik,
          font_weight: 700,
          font_size: LayoutScaler.textSizeFromDesign( 35 ),
          text_color: ColorScheme.white
        }
      );
      this.addChild( name_text, true );
    }

    if ( this.display_id )
    {
      const only_id = !this.display_name;
      const id_y    = only_id ? 0 : LayoutScaler.yFromDesign( 68 );
      const id_h    = only_id ? this.rect.getH() : LayoutScaler.heightFromDesign( 34 );

      const id_text = new UITextElement(
        this.ctx,
        text_x,
        id_y,
        text_w,
        id_h,
        {
          text: only_id ? this.display_id : `ID: ${ this.display_id }`,
          vertical_align: MyTextVerticalAlign.Center,
          text_align: MyTextAlign.Left,
          text_style: TextStyleScheme.rubik,
          font_weight: only_id ? 700 : 400,
          font_size: LayoutScaler.textSizeFromDesign( only_id ? 35 : 22 ),
          text_color: ColorScheme.white
        }
      );
      this.addChild( id_text, true );
    }
  }

  private createEmptySectionBg(): void
  {
    const empty_section_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "rgba(0, 0, 0, 0)",
      undefined,
      false,
      "rgba(255, 255, 255, 0.3)",
      LayoutScaler.widthFromDesign( 4 )
    );

    this.addChild( empty_section_background, true );
  }

  private createPlusIcon(): void
  {
    const plus_size = LayoutScaler.widthFromDesign( 46 );
    const plus_x = ( this.rect.getW() - plus_size ) / 2;
    const plus_y = ( this.rect.getH() - plus_size ) / 2;

    const plus_icon = new UIImage(
      this.ctx,
      plus_x,
      plus_y,
      plus_size,
      plus_size,
      ImgID.PlusIconWhite
    );

    this.addChild( plus_icon, true );
  }

  private getProviderIcon( provider: LinkedSocialProvider ): ImgID
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Facebook: return ImgID.FacebookIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UISocialMediaSection";
  }
}

class UIExitFromAccountSection extends UIElement
{
  private exit_button!: RoundedRectangle;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  )
  {
    super( ctx, x, y, w, h );
    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createExitButton();
    this.createUserIdText();
  }

  private createBackground(): void
  {
    const exit_background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 75 ),
      "rgba(255, 255, 255, 0.3)",
    );

    this.addChild( exit_background );
  }

  private createExitButton(): void
  {
    const btn_y = LayoutScaler.yFromDesign( 77 );
    const btn_w = LayoutScaler.widthFromDesign( 727 );
    const btn_x = ( this.rect.getW() - btn_w ) / 2;
    const btn_h = LayoutScaler.heightFromDesign( 110 );
    this.exit_button = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      LayoutScaler.cornerRadiusFromDesign( 70 ),
      "#0080FF",
      undefined,
      true
    );
    this.exit_button.setOnClick( () =>
    {
      DialogManager.openDialog( new ExitFromGameAccountDialog() );
    } );

    this.addChild( this.exit_button );

    const text_w = btn_w;
    const text_h = btn_h;

    const exit_btn_text = new UITextElement(
      this.ctx,
      btn_x,
      btn_y,
      text_w,
      text_h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 47 ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );

    this.addChild( exit_btn_text, true );
  }

  private createUserIdText(): void
  {
    const user_id = AuthSessionStore.getPlayerId() ?? 0;

    const text_y = LayoutScaler.yFromDesign( 17 );
    const text_w = LayoutScaler.widthFromDesign( 444 );
    const text_x = ( this.rect.getW() - text_w ) / 2;
    const text_h = LayoutScaler.heightFromDesign( 40 );

    const user_id_text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        text: `user_id: ${ user_id }`,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 700,
        font_size: LayoutScaler.textSizeFromDesign( 30 )
      }
    );

    this.addChild( user_id_text );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UIExitFromAccountSection";
  }
}

export class AccountProgressSavedDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.ACCOUNT_PROGRESS_SAVED_DIALOG;
  private bg!: RoundedRectangle;

  constructor( private linked_providers: LinkedSocialProvider[] =  getLinkedSocialProviders() )
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 800 );
    const body_h = LayoutScaler.heightFromDesign( 1113 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTitle();
    this.createSocialMediaSections();
    this.createDeleteAccountText();
    this.createExitFromAccountSection();
    this.createCloseIcon();
  }

  private createSocialMediaSections(): void
  {
    const section_w = LayoutScaler.widthFromDesign( 760 );
    const section_h = LayoutScaler.heightFromDesign( 110 );
    const section_x = ( this.rect.getW() - section_w ) / 2;
    const first_section_y = LayoutScaler.yFromDesign( 170 );
    const section_gap = LayoutScaler.yFromDesign( 18 );

    const sections: Array<{ mode: SocialSectionMode; provider?: LinkedSocialProvider; on_click?: () => void; display_name?: string; display_id?: string }> =
      this.linked_providers.slice( 0, 3 ).map( provider =>
      {
        const info = getLinkedProviderInfo( provider );
        const email_only = provider === LinkedSocialProvider.Google || provider === LinkedSocialProvider.Apple;
        return {
          mode: SocialSectionMode.Linked,
          provider,
          on_click:     () => { DialogManager.openDialog( new DisconnectAccountDialog( provider, () => { void this.disconnectProvider( provider ); } ) ); },
          display_name: email_only ? undefined : ( info?.name ?? undefined ),
          display_id:   email_only
            ? ( info?.email ?? AccountProgressSavedDialog.getCachedIdForProvider( provider ) ?? undefined )
            : ( AccountProgressSavedDialog.getCachedIdForProvider( provider ) ?? undefined ),
        };
      } );

    if ( sections.length < 3 )
    {
      sections.push( {
        mode: SocialSectionMode.Empty,
        on_click: () =>
        {
          console.log( "[AccountProgressSavedDialog] Plus social section clicked" );
          DialogManager.openDialog( new SaveDialog( undefined, this.linked_providers ) );
        }
      } );
    }

    sections.forEach( ( section, index ) =>
    {
      const section_y = first_section_y + index * ( section_h + section_gap );
      const social_media = new UISocialMediaSection(
        this.ctx,
        section_x,
        section_y,
        section_w,
        section_h,
        section.mode,
        section.provider,
        section.on_click,
        section.display_name,
        section.display_id,
      );

      this.addChild( social_media );
    } );
  }

  private static getCachedIdForProvider( provider: LinkedSocialProvider ): string | null
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   return localStorage.getItem( 'web_google_id' );
      case LinkedSocialProvider.Facebook: return localStorage.getItem( 'web_facebook_id' );
      case LinkedSocialProvider.Apple:    return localStorage.getItem( 'native_apple_id' );
    }
  }

  private static toAuthProvider( provider: LinkedSocialProvider ): AuthProvider
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   return AuthProvider.Google;
      case LinkedSocialProvider.Facebook: return AuthProvider.Facebook;
      case LinkedSocialProvider.Apple:    return AuthProvider.Apple;
    }
  }

  private async disconnectProvider( provider: LinkedSocialProvider ): Promise<void>
  {
    // const session = AuthSessionStore.get();
    // if ( !session ) return;

    let player;
    try
    {
      player = await disconnectProviderApi( AccountProgressSavedDialog.toAuthProvider( provider ) );
    }
    catch ( e )
    {
      if ( e instanceof ApiError && e.status === 400 &&
           ( e.body as any )?.error === 'Cannot disconnect the last linked account' )
      {
        DialogManager.routeToDialog( new FailedDisconnectSocialMediaDialog( provider ) );
        return;
      }
      else
      {
        switch ( provider )
        {
          case LinkedSocialProvider.Google:
            await GoogleSignInPlugin.signOut();
            break;
          case LinkedSocialProvider.Facebook:
            await FacebookSignInPlugin.signOut();
            break;
          case LinkedSocialProvider.Apple:
          {
            const isNative = ( window as any ).Capacitor?.getPlatform?.() === 'ios' ||
                             ( window as any ).Capacitor?.getPlatform?.() === 'android';
            await ( isNative ? AppleSignInPlugin : AppleWebSignIn ).signOut();
            break;
          }
        }
      }
      throw e;
    }

    resetLinkedSocialProviders();
    initLinkedProvidersFromServer( player.linked_providers ?? [] );
    console.log( `[AccountProgressSavedDialog] Disconnected provider: ${ provider }` );

    // if ( session.provider === AccountProgressSavedDialog.toAuthProvider( provider ) )
    // {
    //   const google_id   = await GoogleSignInPlugin.getCachedId();
    //   const facebook_id = google_id   ? null : await FacebookSignInPlugin.getCachedId();
    //   const apple_id    = facebook_id ? null : await AppleSignInPlugin.getCachedId();

    //   let new_provider: AuthProvider;
    //   let new_provider_id: string;

    //   if ( google_id )        { new_provider = AuthProvider.Google;   new_provider_id = google_id; }
    //   else if ( facebook_id ) { new_provider = AuthProvider.Facebook; new_provider_id = facebook_id; }
    //   else if ( apple_id )    { new_provider = AuthProvider.Apple;    new_provider_id = apple_id; }
    //   else
    //   {
    //     const android_id = await GoogleSignInPlugin.getAndroidId();
    //     new_provider = AuthProvider.Guest;
    //     new_provider_id = android_id ?? localStorage.getItem( 'analytics_userid' ) ?? '';
    //   }

    //   AuthSessionStore.setFromPlayer( player, new_provider, new_provider_id );
    //   console.log( `[AccountProgressSavedDialog] Session updated to provider: ${ new_provider }` );
    // }

    if ( getLinkedSocialProviders().length > 0 )
    {
      DialogManager.routeToDialog( new AccountProgressSavedDialog() );
      return;
    }

    DialogManager.closeAfterTransition();
  }

  private createTitle(): void
  {
    const label_x = LayoutScaler.xFromDesign( 121 );
    const label_y = LayoutScaler.yFromDesign( 27.4 );
    const label_w = LayoutScaler.widthFromDesign( 558 );
    const label_h = LayoutScaler.heightFromDesign( 121 );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "PROGRESS_SAVED_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 700,
        auto_wrap: true,
        vertical_align: MyTextVerticalAlign.Center
      }
    );

    this.addChild( label );
  }

  private createBackground(): void
  {
    this.bg = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( 30 ),
        tr: LayoutScaler.cornerRadiusFromDesign( 30 ),
        br: LayoutScaler.cornerRadiusFromDesign( 70 ),
        bl: LayoutScaler.cornerRadiusFromDesign( 70 )
      },
      "#890249"
    );
    this.addChild( this.bg );
  }

  private createExitFromAccountSection(): void
  {
    const section_y = LayoutScaler.yFromDesign( 700 );
    const section_w = LayoutScaler.widthFromDesign( 760 );
    const section_x = ( this.rect.getW() - section_w ) / 2;
    const section_h = LayoutScaler.heightFromDesign( 201 );

    const section = new UIExitFromAccountSection(
      this.ctx,
      section_x,
      section_y,
      section_w,
      section_h,
    );

    this.addChild( section );
  }

  private createDeleteAccountText(): void
  {
    const del_account_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 1007 ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 40 ),
      {
        localization_key: "PROGRESS_SAVED_DIALOG.ACCOUNT_DELETE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    del_account_text.setOnClick( () =>
    {
      DialogManager.openDialog( new DeleteAccountDialog() );
    } );
    this.addChild( del_account_text );
  }

  private createCloseIcon(): void
  {
    const icon_size = LayoutScaler.widthFromDesign( 65 );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 39.8 );
    const icon = new UIImage(
      this.ctx,
      icon_x,
      LayoutScaler.yFromDesign( 52.4 ),
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () => { DialogManager.closeDialog(); } );
    this.addChild( icon );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "AccountProgressSavedDialog";
  }
}
